/**
 * Switch units that PlanChangeFlow switched off back on, and keep the
 * session memory honest about what really happened:
 *   changed  — switched on now (forgotten)
 *   missing  — no longer exist / in the trash (forgotten, nothing to do)
 *   stillOff — exist but did not change (kept, so the host can retry)
 * Throws when the update itself failed (memory untouched).
 */
import type { QueryClient } from '@tanstack/react-query';
import { keepDeactivatedUnits, readDeactivatedUnits } from '@/lib/flowDeactivatedUnits';
import { existingUnitIds, setUnitsActive } from './DowngradeModal';

export interface SwitchBackOutcome {
  changed: string[];
  missing: string[];
  stillOff: string[];
}

export async function switchUnitsBackOn(
  userId: string | null | undefined,
  ids: string[],
  queryClient?: QueryClient,
): Promise<SwitchBackOutcome> {
  if (!ids.length) return { changed: [], missing: [], stillOff: [] };
  const changed = await setUnitsActive(ids, true, queryClient);
  const rest = ids.filter((id) => !changed.includes(id));
  let missing: string[] = [];
  if (rest.length) {
    try {
      const exist = await existingUnitIds(rest);
      missing = rest.filter((id) => !exist.includes(id));
    } catch (e) {
      // Can't tell — treat them as existing, so the host can try again.
      console.warn('Checking units failed:', e);
    }
  }
  const stillOff = rest.filter((id) => !missing.includes(id));
  const done = new Set([...changed, ...missing]);
  keepDeactivatedUnits(userId, readDeactivatedUnits(userId).filter((id) => !done.has(id)));
  return { changed, missing, stillOff };
}
